<template>
    <div
        class="timer-container d-flex align-items-center"
        :class="{ pointer: timerIsClickable }"
        @click="clickTimer()"
        ref="timer"
    >
        <div v-show="timerIsClickable">
            <span class="icon icon-glyphicons-basic-circle-alert mr-1 text-danger align-middle"></span>
        </div>
        <div class="mr-2" v-if="timer.node_timer.show_label">
            <span class="badge badge-light"> {{ timer.node_timer.label }}: </span>
        </div>
        <div
            class="
                font-weight-bolder
                timer-display
                flex-grow-1
                justify-content-end
                d-flex
                align-items-center align-middle
                blink
            "
            :class="{ 'text-danger': countdownDanger & isCountDown }"
        >
            <span class="icon icon-glyphicons-basic-stopwatch timer-icon align-middle"></span>
            <div class="align-middle" :class="{ 'stopwatch-display': !isClockDisplay, 'clock-display': isClockDisplay, 'text-secondary': isLoading }">
                {{ displayedTime }}
            </div>
        </div>
    </div>
</template>

<script>
import { set, differenceInSeconds, addSeconds, format } from "date-fns"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "NodeTimer",
    props: {
        timer: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            timeoutId: null,
            displayedTime: "",
            secondsRemaining: null,
            timerIsExpired: false,
            timerStarted: null,
            isLoading: false,
        }
    },
    computed: {
        isClockDisplay() {
            return this.timer.node_timer.display_type === "clock"
        },
        isCountDown() {
            return this.timer.node_timer.display_type === "count_down"
        },
        // Returns true if the timer has almost reached its limit.
        countdownDanger() {
            return this.timer.node_timer.time_limit_seconds !== null && this.secondsRemaining < 10
        },
        timerIsClickable() {
            return this.timerIsExpired && this.timer.node_timer.action === "proceed_when_clicked"
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        elapsedSeconds() {
            return this.timer.elapsed_seconds
        },
    },
    watch: {
        elapsedSeconds: function(newValue) {
            // Reset the timer started timestamp if the elapsed seconds value is updated. This
            // means the timer was refreshed from the server.
            this.timerStarted = new Date()
        },
    },
    methods: {
        updateTimer() {
            let nodeTimer = this.timer.node_timer
            let now = new Date()
            // The total elapsed seconds is equal to the persisted elapsed seconds for the timer, plus the time
            // that this component has been active and visible to the learner.
            let secondsElapsed = Math.round(this.timer.elapsed_seconds) + differenceInSeconds(now, this.timerStarted)

            // Timer display logic.
            if (nodeTimer.display_type === "count_up") {
                this.displayedTime = this.formatStopwatch(secondsElapsed)
            } else if (nodeTimer.display_type === "count_down") {
                let secondsRemaining = nodeTimer.time_limit_seconds - secondsElapsed
                let secondsRemainingFloor = secondsRemaining < 0 ? 0 : secondsRemaining
                this.displayedTime = this.formatStopwatch(secondsRemainingFloor)
            } else if (nodeTimer.display_type === "clock") {
                // Parse the configured clock start time.
                let startTimeComponents = nodeTimer.clock_start_time.split(":")
                let timerStartHours = startTimeComponents[0]
                let timerStartMinutes = startTimeComponents[1]
                let timerStartSeconds = startTimeComponents[2]
                let startClockTime = set(new Date(), {
                    hours: timerStartHours,
                    minutes: timerStartMinutes,
                    seconds: timerStartSeconds,
                })
                // Add the interval.
                let currentClockTime = addSeconds(startClockTime, secondsElapsed)
                this.displayedTime = format(currentClockTime, "h:mm:ss aa")
            }

            // Timeout action logic.
            if (nodeTimer.time_limit_seconds) {
                this.secondsRemaining = nodeTimer.time_limit_seconds - secondsElapsed
                if (this.secondsRemaining <= 0 && !this.timerIsExpired) {
                    this.handleTimeout()
                }
            }

            this.updateTimerTimeout()
        },
        handleTimeout() {
            this.timerIsExpired = true
            if (this.timer.node_timer.action == "proceed_to_node") {
                // Emit an event that the timer has expired.
                this.$emit("timer-expired", this.timer.id)
            } else if (this.timer.node_timer.action === "proceed_when_clicked") {
                // Show popover for a few seconds telling the learner to click on the timer.
                $(this.$refs.timer).popover({
                    content: this.labelsTranslated.clickToAddress,
                    trigger: "hover",
                    placement: "top",
                    template: `
                        <div class="popover hotspot-popover shadow-sm" role="tooltip">
                            <div class="arrow"></div>
                            <h3 class="popover-header"></h3>
                            <div class="p-2 text-center">
                                <span class="icon icon-glyphicons-basic-circle-alert text-danger align-middle"></span>
                                <span class="popover-body align-middle"></span>
                            </div>
                        </div>
                    `,
                })
                $(this.$refs.timer).popover("show")
                let vm = this
                window.setTimeout(function () {
                    $(vm.$refs.timer).popover("hide")
                }, 2100)
            }
        },
        formatStopwatch(secondsElapsed) {
            return Utilities.formatStopwatch(secondsElapsed)
        },
        updateTimerTimeout() {
            let vm = this
            this.timeoutId = window.setTimeout(function () {
                vm.updateTimer()
            }, 1000)
        },
        clickTimer() {
            if (!this.timerIsExpired) {
                // Don't do anything if the timer hasn't expired yet.
                return
            }
            this.isLoading = true
            // Emit an event that the timer has expired.
            this.$emit("timer-expired", this.timer.id)
        },
        
    },
    mounted() {
        this.timerStarted = new Date()
        // Initiate the per-second update of the timer display.
        this.updateTimer()
    },
    destroyed() {
        // Stop the timeout loop.
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
        }
    },
    beforeDestroy() {
        $(this.$refs.timer).popover("hide")
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.timer-display {
    font-size: 22px;
    @media (min-width: $breakpoint-sm) {
        font-size: 24px;
    }
    color: #3d444a;
    max-height: 35px;
}

.stopwatch-display {
    min-width: 65px;
    @media (min-width: $breakpoint-sm) {
        min-width: 73px;
    }
}

.clock-display {
    min-width: 140px;
}

.timer-container {
    margin-top: -5px;
    margin-bottom: -5px;
}

.timer-icon {
    margin-top: 3px;
    margin-right: 4px;
    font-size: 100%;
}

.blink {
    animation-name: blinking;
    animation-duration: 0.45s;
    animation-iteration-count: 2;
    animation-timing-function: ease-out;
}

@keyframes blinking {
    50% {
        opacity: 0;
    }
}
</style>